import {
  employeeListResponseSchema,
  employeeResponseSchema,
  type CreateEmployeeRequest,
  type EmployeeListResponse,
  type UpdateEmployeeRequest,
} from '@coworker/contracts';
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { useAuth } from '../auth/AuthProvider.js';
import { useRecoverSession } from './use-recover-session.js';
import { ApiError } from '../lib/api-error.js';
import { useCurrentWorkspace } from './use-current-workspace.js';

/**
 * The workspace's employees - EMP-005, EMP-006.
 *
 * Keyed by workspace id, so switching workspaces is a different cache entry
 * rather than a stale list under a new heading. WEB-006 makes that switch a
 * thing a person can do; the key is right before it exists.
 */
export const employeesQueryKey = (workspaceId: string | undefined) =>
  ['employees', workspaceId] as const;

export interface EmployeesState {
  readonly query: UseQueryResult<EmployeeListResponse, unknown>;
  readonly isUnauthenticated: boolean;
}

export function useEmployees(): EmployeesState {
  const { api, status } = useAuth();
  const workspace = useCurrentWorkspace();

  const query = useQuery({
    queryKey: employeesQueryKey(workspace?.id),
    // Gated on the workspace as well as the session: without an id the request
    // would go out with no `x-workspace-id` and come back 400, which would read
    // as a failure when the truth is that there is nothing to ask about yet.
    enabled: status === 'signedIn' && workspace !== undefined,
    queryFn: () =>
      api.request('/v1/employees', {
        schema: employeeListResponseSchema,
        workspaceId: workspace?.id ?? '',
      }),
  });

  const isUnauthenticated = query.error instanceof ApiError && query.error.isUnauthenticated;

  // A lapsed token on this screen is recovered here, not only in `AppShell`.
  // This value was computed and returned and read by nobody, so a 401 on this
  // query dead-ended on an error card whose retry re-sent the same token.
  useRecoverSession({ isUnauthenticated, erroredAt: query.errorUpdatedAt });

  return {
    query,
    isUnauthenticated,
  };
}

/** Hires a Specialist. Type and status are the API's to decide, never ours. */
export function useCreateEmployee() {
  const { api } = useAuth();
  const workspace = useCurrentWorkspace();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateEmployeeRequest) =>
      api.request('/v1/employees', {
        method: 'POST',
        schema: employeeResponseSchema,
        workspaceId: workspace?.id ?? '',
        body: input,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: employeesQueryKey(workspace?.id) }),
  });
}

/**
 * Edits a profile - EMP-007.
 *
 * **The reason this matters more than a form usually does.** `instructionsFor()`
 * in the agent runtime builds the model's system prompt from `name`,
 * `roleTitle` and `jobDescription`, and until this existed the third could not
 * be written from a browser at all: every employee anybody created was
 * prompt-identical apart from a name and a title, and a specialist in sales and
 * one in recruiting answered the same way. With tools arriving they would also
 * *choose* the same way.
 *
 * A partial body: `updateEmployeeRequestSchema` makes every field optional and
 * the repository coalesces the absent ones, so this sends only what changed.
 */
export function useUpdateEmployee() {
  const { api } = useAuth();
  const workspace = useCurrentWorkspace();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ employeeId, edit }: { employeeId: string; edit: UpdateEmployeeRequest }) =>
      api.request(`/v1/employees/${employeeId}`, {
        method: 'PATCH',
        schema: employeeResponseSchema,
        workspaceId: workspace?.id ?? '',
        body: edit,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: employeesQueryKey(workspace?.id) }),
  });
}

/**
 * The one transition this screen offers - EMP-003.
 *
 * `DRAFT -> PROBATION` and nothing else, because it is the only forward edge out
 * of DRAFT and so needs no copy of the rules in the browser. The lifecycle table
 * lives in `packages/domain`, which is `runtime:node` and which ADR-0022 forbids
 * this project from importing; duplicating it here to draw more buttons would
 * put an authorization-shaped rule in two places, and the second one would be
 * the one nobody updates. EMP-007 owns the rest of the lifecycle UI and has to
 * solve that properly.
 */
export function useHireEmployee() {
  const { api } = useAuth();
  const workspace = useCurrentWorkspace();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (employeeId: string) =>
      api.request(`/v1/employees/${employeeId}/status`, {
        method: 'POST',
        schema: employeeResponseSchema,
        workspaceId: workspace?.id ?? '',
        body: { status: 'PROBATION' },
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: employeesQueryKey(workspace?.id) }),
  });
}
